import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import camelcaseKeys from 'camelcase-keys';
import * as QueryString from 'query-string';
import Container from '@material-ui/core/Container';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import ProgressBar from './components/ProgressBar';
import FlowButton from '../components/FlowButton';
import { ProviderAPI } from 'api';
import { getServicesWithMapping } from '../utils';
import Loader from 'components/Loader';
import {
  DIRECT_CONNECT_FIELD,
  EMAIL_FIELD,
  FIRST_NAME_FIELD,
  LAST_NAME_FIELD, PARTICIPANT_EMAIL_FIELD,
  PARTICIPANT_FIRST_NAME_FIELD, PARTICIPANT_LAST_NAME_FIELD, PARTICIPANT_PHONE_FIELD,
  PHONE_FIELD, SERVICE_FOR_TYPE_MYSELF
} from '../../constants';
import Card from './components/Card';
import style from 'style';
import { AuthStore, TaxonomyStore } from 'store';


const useStyles = makeStyles(theme => ({
  flowContainer: {
    minHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px 20px 40px 20px'
  },
  children: {
    marginTop: '30px',
  },
  directRequestContainer: {
    marginTop: '30px',
    marginBottom: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  directRequestHeader: {
    width: '350px',
    textAlign: 'center',
    background: theme.palette.common.white,
    border: '1px solid',
    borderColor: theme.palette.common.lightBorder,
    borderRadius: '12px',
    boxShadow: theme.shadow(15),
    padding: '10px',
    marginBottom: '10px',
    fontSize: '15px',
    fontWeight: '500',
    color: theme.palette.primary.main,
  },
  coloredText: {
    color: theme.palette.secondary.main,
  },
  buttonContainer: {
    marginTop: 'auto',
    display: 'flex',
    justifyContent: ({isFinalPage}) => isFinalPage ? 'flex-end' : 'space-between',
    columnGap: ({isFinalPage}) => isFinalPage ? '38px' : '0',
  },
}));

const QuestionFlowContainer = (
  {
    children,
    flow,
    answers,
    setAnswers,
    setPagename,
    currentPage,
    next,
    prev,
    complete,
    progress,
    progressAction
  }) => {

  const location = useLocation();
  const urlParams = QueryString.parse(location.search);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const servicesTaxonomy = useSelector(state => state.taxonomy.services);
  const qualifiedServicesTaxonomy = useSelector(state => state.taxonomy.qualifiedServices);

  const [progressbarPageValues, setProgressbarPageValues] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const history = useHistory();

  const dispatch = useDispatch();
  const profile = useSelector(state => camelcaseKeys(state.auth.profile, {deep: true}));

  const directRequestServiceUuid = urlParams['service_offering'];

  const classes = useStyles({isFinalPage: !next, isDirectConnect: !!directRequestServiceUuid});

  const updateAnswer = (field, value) => setAnswers(
    prevState => ({...prevState, [field]: value})
  );

  const populateDataFromProfile = () => {

    const updateAnswers = data => {
      for (const {profileKey, answersKey} of data) {
        if (!profile || !(profileKey in profile) || !profile[profileKey] || !!answers[answersKey])
          continue;
        updateAnswer(answersKey, profile[profileKey]);
      }
    };

    const contactData = [
      {profileKey: 'firstName', answersKey: FIRST_NAME_FIELD},
      {profileKey: 'lastName', answersKey: LAST_NAME_FIELD},
      {profileKey: 'contactPhone', answersKey: PHONE_FIELD},
      {profileKey: 'email', answersKey: EMAIL_FIELD},
    ];

    const participantData = [
      {profileKey: 'firstName', answersKey: PARTICIPANT_FIRST_NAME_FIELD},
      {profileKey: 'lastName', answersKey: PARTICIPANT_LAST_NAME_FIELD},
      {profileKey: 'contactPhone', answersKey: PARTICIPANT_PHONE_FIELD},
      {profileKey: 'email', answersKey: PARTICIPANT_EMAIL_FIELD},
    ];

    updateAnswers(contactData);
    answers?.servicesFor === SERVICE_FOR_TYPE_MYSELF && updateAnswers(participantData);
  };

  useEffect(() => {
    dispatch(TaxonomyStore.refreshTaxonomy(TaxonomyStore.QUALIFIED_SERVICE_TAXONOMY));
    dispatch(AuthStore.refreshProfile());
  }, []);

  useEffect(() => {
    populateDataFromProfile();
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (directRequestServiceUuid) {
      ProviderAPI.getListing(directRequestServiceUuid)
        .then(r => camelcaseKeys(r.payload, {deep: true}))
        .then(r => {
          const { serviceMap } = getServicesWithMapping(
            camelcaseKeys(qualifiedServicesTaxonomy, {deep: true}),
            camelcaseKeys(servicesTaxonomy, {deep: true})
          );
          // If the listing does not have direct connect access ad feature then
          // the regular flow will be loaded.
          if (r?.hasDirectConnectAccess === false) {
            history.push('/requestmatch');
            return;
          }
          setAnswers(prevState => {
            const servicesNeeded = [serviceMap[r.service]];
            return {...prevState, [DIRECT_CONNECT_FIELD]: r, servicesNeeded: servicesNeeded};
          });
        })
        .then(() => setIsLoading(false))
        .catch(err => console.error(err));  // eslint-disable-line no-console
    } else {
      setIsLoading(false);
    }
  }, [servicesTaxonomy, qualifiedServicesTaxonomy]);


  useEffect(() => {
    const validPages = flow.pages.filter(item => (
      item?.progressbar && (!item.isHidden || !item.isHidden(answers))));

    const progressValues = validPages.reduce((acc, item, index) => {
      return {...acc, [item.name]: index};
    }, {});
    setProgressbarPageValues(progressValues);
  }, [flow, answers[DIRECT_CONNECT_FIELD]]);

  useEffect(() => {
    if (currentPage?.progressbar) {
      progressAction.set(progressbarPageValues[currentPage.name]);
    } else {
      progressAction.set(Object.keys(progressbarPageValues).length);
    }

  }, [currentPage, progressbarPageValues, progressAction]);

  useEffect(() => {
    window.scrollTo({
      top: isMobile && directRequestServiceUuid && !!prev ? 480 : 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [currentPage]);

  if (isLoading) return <Loader />;

  const renderFlowControlButtons = () => {

    const nextButtonText = currentPage?.flowControlButtonsCustomText?.next ?
      currentPage?.flowControlButtonsCustomText?.next : 'Next';

    const backButtonText = currentPage?.flowControlButtonsCustomText?.back ?
      currentPage?.flowControlButtonsCustomText?.back : 'Back';

    return (
      <>
        {currentPage?.hideBackButton ? <>&nbsp;</> : (
          <FlowButton
            onClick={() => {
              if (!prev)
                history.push('');
              else {
                setPagename(prev.name);
              }
            }}
            type="back"
            label={backButtonText}
            pointer="none"
            variant={null}
          />
        )}
        <FlowButton
          onClick={()=> {
            setPagename(next.name);
          }}
          label={nextButtonText}
          pointer="none"
          disabled={!complete}
        />
      </>
    );
  };

  return (
    <Container maxWidth={currentPage.fullWidth ? 'lg' : 'md'} fixed disableGutters={true}>
      <div className={classes.flowContainer}>
        <div>
          {(currentPage?.progressbar || currentPage?.progressbarShowOnly) && (
            <ProgressBar value={(progress / Object.keys(progressbarPageValues).length) * 100} />
          )}
        </div>
        {answers[DIRECT_CONNECT_FIELD] && !currentPage.isEndOfJourney && (
          <div className={classes.directRequestContainer}>
            <div className={classes.directRequestHeader}>
              You will be directly connected with <span style={{color: style.colors.secondary}}>{answers[DIRECT_CONNECT_FIELD]?.providerName}</span>
            </div>
            <Card
              title={answers[DIRECT_CONNECT_FIELD]?.service}
              imagePath={answers[DIRECT_CONNECT_FIELD]?.imagePath}
            />
          </div>
        )}
        <div className={classes.children}>
          {children}
        </div>
        <div className={classes.buttonContainer}>
          {next && (currentPage?.flowControlButtons && renderFlowControlButtons())}
        </div>
      </div>
    </Container>
  );
};

QuestionFlowContainer.propTypes = {
  children: PropTypes.node.isRequired,
  flow: PropTypes.object.isRequired,
  answers: PropTypes.object,
  setAnswers: PropTypes.func.isRequired,
  setPagename: PropTypes.func.isRequired,
  currentPage: PropTypes.object.isRequired,
  next: PropTypes.object,
  prev: PropTypes.object,
  complete: PropTypes.bool,
  progress: PropTypes.number,
  progressAction: PropTypes.object.isRequired,
};

export default QuestionFlowContainer;